// Role shell: approved sidebar/topbar structure with navigation derived from server-granted
// experiences. Hiding a link is convenience only; the API enforces every action independently.
import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { LanguageSwitch, UnreviewedNotice, useI18n } from '../i18n.tsx';
import { navFor, ROLE_COPY } from '../nav.ts';
import { setWorkingSite } from '../api.ts';
import { useSession } from '../session.tsx';
import { Brand, Icon } from './ui.tsx';

const SITE_KEY = 'passage.workingSite';

/**
 * Working-site context (PB-12-07). A per-viewer convenience, never an authority: one shared store per
 * signed-in scope, so every open page sees a switch at once (PB-02-06), and the API client sends it on
 * site-bound commands so the server refuses a stale page acting on another site (PD-066).
 */
const siteListeners = new Set<() => void>();
const siteMemory = new Map<string, string | null>();
function readSite(key: string): string | null {
  if (!siteMemory.has(key)) {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(key);
    } catch {
      /* storage unavailable */
    }
    siteMemory.set(key, stored);
  }
  return siteMemory.get(key) ?? null;
}
function writeSite(key: string, id: string): void {
  siteMemory.set(key, id);
  try {
    localStorage.setItem(key, id);
  } catch {
    /* storage unavailable: keep in memory only */
  }
  for (const l of siteListeners) l();
}
function subscribeSite(l: () => void): () => void {
  siteListeners.add(l);
  // Another tab switching site updates this one too.
  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith(SITE_KEY)) {
      siteMemory.delete(e.key);
      l();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    siteListeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

export function useWorkingSite(): [string | null, (id: string) => void] {
  const { session, scopeKey } = useSession();
  const storageKey = `${SITE_KEY}:${scopeKey.join(':')}`;
  const siteId = useSyncExternalStore(subscribeSite, () => readSite(storageKey));
  const valid = session.sites.some((s) => s.id === siteId) ? siteId : (session.sites.find((s) => s.status === 'active')?.id ?? session.sites[0]?.id ?? null);
  setWorkingSite(valid);
  return [valid, (id: string) => writeSite(storageKey, id)];
}

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function useSiteName(): (id: string | null) => string {
  const { session } = useSession();
  const { t } = useI18n();
  return (id) => (id ? (session.sites.find((s) => s.id === id)?.name ?? t('common.unavailableSite')) : t('common.allSites'));
}

export function Shell({ children }: { children: ReactNode }) {
  const { session, signOut, canAnywhere } = useSession();
  const { t, tk, locale, reviewed } = useI18n();
  const [siteId, chooseSite] = useWorkingSite();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const now = useClock();
  const siteName = useSiteName();
  const experienceIds = useMemo(() => [...new Set(session.experiences.map((e) => e.id))], [session.experiences]);
  // Capability-derived entries (PD-018 pattern). Contractors stream (PD-155): hosts reach their own
  // deliveries, site administrators the handover areas, organisation administrators the evidence rules.
  const extraRoutes = [
    ...(canAnywhere('user.directory.read') ? ['users'] : []),
    ...(canAnywhere('delivery.read.own') || canAnywhere('delivery.point.manage') ? ['deliveries'] : []),
    ...(canAnywhere('contractor.requirements.manage') ? ['contractors'] : []),
    // PD-246 (safety & reporting stream): reception reads live occupancy and reviews presence; site
    // administrators keep safety settings; operators register presence feeds; report grants open reports.
    ...(canAnywhere('reception.queue.read') || canAnywhere('site.update') || canAnywhere('integration.manage') ? ['safety'] : []),
    ...(canAnywhere('report.aggregate.read') ? ['summary'] : []),
    // ADR-020 (PD-267): organisation integration records for administrators; privacy specialists.
    ...(canAnywhere('integration.org.read') ? ['integrations'] : []),
    ...(canAnywhere('retention.manage') || canAnywhere('privacy.hold.release') || canAnywhere('privacy.purge.apply') ? ['privacy'] : []),
    // ADR-017 (PD-198): verifiers and identity administrators reach Identity verification.
    ...(canAnywhere('identity.review') || canAnywhere('verification.settings.manage') || canAnywhere('verification.provider.manage') ? ['identity'] : []),
    // ADR-018 (credentials stream): credential issuers and badge/card administrators reach Badges & access.
    ...(canAnywhere('credential.issue') || canAnywhere('credential.reconcile') || canAnywhere('card.manage') || canAnywhere('badge.template.manage') ? ['credentials'] : []),
    // PD-160: parking/registry administration is reached through the approved Vehicles & gates entry.
    ...(canAnywhere('parking.manage') || canAnywhere('vehicle.manage') ? ['vehicles'] : []),
    // PD-047: template managers and delivery-log readers reach Communications.
    ...(canAnywhere('template.manage') || canAnywhere('message.read') ? ['communications'] : []),
    // ADR-024 (PD-311): AI assistance for entitled users, AI administrators and organisation oversight.
    ...(['ai.reporting.use', 'ai.decision_support.use', 'ai.settings.read', 'ai.settings.manage', 'ai.activity.read'].some((x) => canAnywhere(x)) ? ['ai'] : []),
    // PD-305: restricted screening workspace (security supervisors, screening list managers).
    ...(canAnywhere('screening.case.review') || canAnywhere('screening.list.manage') ? ['screening'] : []),
    // PD-379 (ADR-028): organisation register readers and hosts' reassignment queue.
    ...(canAnywhere('organisation.read') || canAnywhere('organisation.manage') || canAnywhere('host.manage') || canAnywhere('visit.read.delegated') ? ['people'] : []),
  ];
  const items = navFor(experienceIds, extraRoutes);
  const site = session.sites.find((s) => s.id === siteId) ?? null;
  const scopeLine = session.experiences.map((e) => `${tk(`role.${e.id}.title`)} · ${siteName(e.siteId)}`).join(locale === 'ar' ? '؛ ' : '; ');
  const initials = session.user.displayName.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const group = experienceIds.length === 1 && ROLE_COPY[experienceIds[0]!] ? tk(`role.${experienceIds[0]!}.title`) : t('shell.yourResponsibilities');

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const onSiteChange = (id: string) => {
    chooseSite(id);
    // On a site-bound page, move to the same page of the newly chosen site; forms of the old site unmount.
    const m = /^\/sites\/[0-9a-f-]{36}(\/.*)?$/.exec(location.pathname);
    if (m) navigate(`/sites/${id}${m[1] ?? ''}`);
  };

  const localTime = site
    ? new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-GB', { timeZone: site.timeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'shortOffset' }).format(now)
    : null;

  return (
    <>
      <a className="skip" href="#workspace">
        {t('shell.skip')}
      </a>
      <aside className={`sidebar${menuOpen ? ' open' : ''}`} aria-label={t('shell.openNav')}>
        <NavLink to="/" className="brand">
          <Brand />
        </NavLink>
        <nav className="navscroll">
          <div className="navgroup">{group}</div>
          {items.map((item) => (
            <NavLink key={item.route} to={item.route === 'home' ? '/' : `/${item.route}`} end={item.route === 'home'} className={({ isActive }) => `navlink${isActive ? ' active' : ''}`}>
              <Icon name={item.icon} />
              {t(item.labelKey)}
              {item.milestone ? <span className="later">{t('shell.planned')}</span> : null}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span>{session.organisation.name}</span>
          <span>{t('shell.footer')}</span>
        </div>
      </aside>
      <div className="shell">
        <header className="topbar">
          <button className="button quiet mobilemenu" type="button" aria-label={t('shell.openNav')} aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>
            <Icon name="menu" />
          </button>
          <NavLink className="role-identity" to="/account" title={t('mfa.account.title')}>
            <div className="avatar" aria-hidden="true">
              {initials}
            </div>
            <div>
              <strong>{session.user.displayName}</strong>
              <div className="role-subtitle">{scopeLine || t('shell.noResponsibilities')}</div>
            </div>
          </NavLink>
          <div className="grow" />
          {session.sites.length > 0 ? (
            <label className="scope">
              {t('shell.workingSite')}
              <select value={siteId ?? ''} onChange={(e) => onSiteChange(e.target.value)}>
                {session.sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.status === 'inactive' ? t('shell.inactiveSuffix') : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {site && localTime ? (
            // PD-631: a named group; assistive technology ignores aria-label on a div without a role.
            <div className="local-time" role="group" aria-label={t('shell.localTimeAt', { site: site.name })}>
              <strong>{localTime}</strong>
              <span data-ltr>{site.timeZone}</span>
            </div>
          ) : null}
          <LanguageSwitch compact />
          <button className="button small" type="button" onClick={() => void signOut().then(() => navigate('/sign-in'))}>
            <Icon name="logout" />
            {t('shell.signOut')}
          </button>
        </header>
        {import.meta.env.DEV || !reviewed ? (
          <div className="env-strip">
            {import.meta.env.DEV ? (
              <>
                <b>{t('shell.envDev')}</b>
                <span>{t('shell.envDevNote')}</span>
              </>
            ) : null}
            <UnreviewedNotice />
          </div>
        ) : null}
        <main id="workspace" tabIndex={-1}>
          {children}
        </main>
      </div>
    </>
  );
}
